// frontend/src/pages/dashboard/TaskComponents/TaskCommentSection.js

import React, { useState, useEffect, memo } from "react";
import {
  Box,
  Typography,
  Button,
  Avatar,
  Chip,
  IconButton,
} from "@mui/material";
import {
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
} from "@mui/icons-material";
import { MentionsInput, Mention } from "react-mentions";
import { toast } from "react-toastify";
import AuthenticatedImage from "./AuthenticatedImage";
import ImagePreview from "./ImagePreview";
import { getAvatarUrl, formatTimeAgo, handlePasteImage } from "../../../utils/imageHelpers";
import { highlightMentions } from "../../../utils/sanitizeContent";

const TaskCommentSection = memo(
  ({
    taskId,
    comments,
    loading,
    onPostComment,
    onDeleteComment,
    users,
    authUser,
    newCommentText,
    setNewCommentText,
    commentFiles,
    setCommentFiles,
    theme,
    hasPermission,
    handleDownload,
    handleImagePreview,
  }) => {
    const [localText, setLocalText] = useState(newCommentText[taskId] || "");

    useEffect(() => {
      setLocalText(newCommentText[taskId] || "");
    }, [newCommentText, taskId]);

    const handleTextChange = (e, newValue) => {
      if (typeof newValue === "string") {
        setLocalText(newValue);
        setNewCommentText((prev) => ({ ...prev, [taskId]: newValue }));
      }
    };

    const handleFileChange = (e) => {
      setCommentFiles((prev) => ({ ...prev, [taskId]: e.target.files }));
    };

    const removeFile = (index) => {
      setCommentFiles((prev) => {
        const existing = prev[taskId] || [];
        const filtered = Array.from(existing).filter((_, i) => i !== index);
        return { ...prev, [taskId]: filtered };
      });
    };

    return (
      <Box
        size={{xs:12}}
        sx={{
          p: 2,
          bgcolor: "#fafafa",
          borderRadius: 2,
          mt: 2,
          boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
          border: "1px solid #e0e0e0",
        }}
      >
        <Box sx={{ mb: 2 }}>
          <MentionsInput
            key={`mention-${taskId}`}
            value={localText}
            onChange={handleTextChange}
            onPaste={(e) => handlePasteImage(e, taskId, setCommentFiles, toast)}
            placeholder="Write a comment... Type @ to mention team members (Paste images with Ctrl+V)"
            markup="@[__display__](__id__)"
            allowSpaceInQuery
            style={{
              control: {
                backgroundColor: "#fff",
                fontSize: 15,
                minHeight: 90,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 12,
                padding: "12px",
                boxSizing: "border-box",
                width: "100%",
              },
              highlighter: { display: "none" },
              input: {
                padding: "12px",
                outline: "none",
                border: "none",
                width: "100%",
                backgroundColor: "transparent",
                color: "inherit",
                fontFamily: "inherit",
                fontSize: "inherit",
              },
              suggestions: {
                list: {
                  backgroundColor: "white",
                  border: "1px solid rgba(0,0,0,0.15)",
                  fontSize: 14,
                  maxHeight: 180,
                  overflowY: "auto",
                  borderRadius: 8,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  zIndex: 9999,
                },
                item: {
                  padding: "8px 12px",
                  borderBottom: "1px solid rgba(0,0,0,0.1)",
                  "&focused": { backgroundColor: "#e6f7ff" },
                  cursor: "pointer",
                },
              },
            }}
          >
            <Mention
              trigger="@"
              markup="@[__display__](__id__)"
              displayTransform={(id, display) => `@${display}`}
              data={
                Array.isArray(users)
                  ? users
                      .filter((u) => u && u.name)
                      .map((u) => ({
                        id: String(u.id),
                        display: u.name.trim(),
                      }))
                  : []
              }
              appendSpaceOnAdd
              style={{
                backgroundColor: "transparent",
                color: theme.palette.primary.main,
                fontWeight: 600,
                padding: "2px 4px",
                borderRadius: 4,
                border: "1px solid #e0e0e0",
              }}
            />
          </MentionsInput>

          {commentFiles[taskId] && Array.from(commentFiles[taskId]).length > 0 && (
            <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
              {Array.from(commentFiles[taskId]).map((file, index) => (
                <ImagePreview
                  key={index}
                  file={file}
                  index={index}
                  onRemove={removeFile}
                />
              ))}
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 1.5,
            }}
          >
            <Button
              component="label"
              variant="outlined"
              startIcon={<AttachFileIcon />}
              size="small"
              sx={{ textTransform: "none" }}
            >
              Attach Files
              <input type="file" multiple hidden onChange={handleFileChange} />
            </Button>
            {commentFiles[taskId] && (
              <Typography variant="caption" color="text.secondary">
                {commentFiles[taskId].length} file(s) selected
              </Typography>
            )}
            <Button
              variant="contained"
              size="small"
              onClick={() => onPostComment(taskId)}
              disabled={!localText?.trim()}
              sx={{ textTransform: "none", fontWeight: 600, minWidth: 70 }}
            >
              Post
            </Button>
          </Box>
        </Box>

        <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
          {loading ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", py: 2 }}
            >
              Loading comments...
            </Typography>
          ) : comments?.length > 0 ? (
            comments.map((comment) => (
              <Box
                key={comment.id}
                sx={{
                  py: 1.5,
                  px: 2,
                  mb: 1,
                  bgcolor: "#f9f9f9",
                  borderRadius: 2,
                  borderLeft: "4px solid #1976D2",
                  "&:hover": { bgcolor: "#f0f0f0" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box sx={{ flexGrow: 1, mr: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Avatar
                        sx={{ width: 24, height: 24 }}
                        src={getAvatarUrl(comment.user?.avatar)}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = null;
                        }}
                      >
                        {!comment.user?.avatar
                          ? comment.user?.name?.charAt(0)
                          : null}
                      </Avatar>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {comment.user?.name}
                      </Typography>
                      {comment.user_id === authUser?.id && (
                        <Chip
                          label="You"
                          size="small"
                          color="primary"
                          sx={{ ml: 0.5 }}
                        />
                      )}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        color: "#000",
                        lineHeight: 1.6,
                      }}
                      dangerouslySetInnerHTML={{
                        __html: highlightMentions(comment.content),
                      }}
                    />
                    {comment.attachments && comment.attachments.length > 0 && (
                      <Box
                        sx={{
                          mt: 1,
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                        }}
                      >
                        {comment.attachments.map((attachment) =>
                          attachment.original_name?.match(
                            /\.(jpg|jpeg|png|gif|webp)$/i
                          ) ? (
                            <Box
                              key={attachment.id}
                              sx={{
                                position: "relative",
                                width: 100,
                                height: 100,
                                borderRadius: 2,
                                overflow: "hidden",
                                border: "1px solid #e0e0e0",
                                cursor: "pointer",
                                "&:hover": { opacity: 0.9 },
                              }}
                              onClick={() => handleImagePreview(attachment)}
                            >
                              <AuthenticatedImage
                                attachment={attachment}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                              />
                              <Box
                                sx={{
                                  display: "none",
                                  width: "100%",
                                  height: "100%",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  bgcolor: "#f5f5f5",
                                }}
                              >
                                <AttachFileIcon color="action" />
                              </Box>
                            </Box>
                          ) : (
                            <Box
                              key={attachment.id}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                p: 1,
                                bgcolor: "#fff",
                                borderRadius: 1,
                                border: "1px solid #ddd",
                                "&:hover": { bgcolor: "#f5f5f5" },
                                maxWidth: 200,
                              }}
                            >
                              <AttachFileIcon fontSize="small" color="action" />
                              <Typography
                                variant="caption"
                                noWrap
                                sx={{
                                  flex: 1,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {attachment.original_name}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleDownload(attachment)}
                                sx={{ color: "text.secondary" }}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )
                        )}
                      </Box>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                    }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      {formatTimeAgo(comment.created_at)}
                    </Typography>
                    {(comment.user_id === authUser?.id ||
                      hasPermission("task.comment.delete")) && (
                      <IconButton
                        size="small"
                        onClick={() => onDeleteComment(taskId, comment.id)}
                        sx={{ mt: 0.5, color: "error.main" }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Box>
            ))
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", py: 2 }}
            >
              No comments yet. Be the first to comment!
            </Typography>
          )}
        </Box>
      </Box>
    );
  }
);

export default TaskCommentSection;